import { useEffect, useState } from 'react';
import { AppProvider, useAppData } from './app/AppProvider';
import { parseHash, type AppRoute } from './app/router';
import { EmptyState } from './components/EmptyState';
import { activateServiceWorker, registerServiceWorker } from './pwa/register';
import { HomePage } from './pages/HomePage';
import { PlaceFormPage } from './pages/PlaceFormPage';
import { PlacePage } from './pages/PlacePage';
import { SettingsPage } from './pages/SettingsPage';
import { StationPage } from './pages/StationPage';

function useRoute(): AppRoute {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));

  useEffect(() => {
    const update = () => {
      setRoute(parseHash(window.location.hash));
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);

  return route;
}

function AppContent() {
  const { loading, error, reload } = useAppData();
  const route = useRoute();

  if (loading) {
    return <main className="page page--centered"><div className="spinner" role="status" aria-label="Загрузка данных" /></main>;
  }

  if (error) {
    return (
      <main className="page page--centered">
        <EmptyState title="Локальные данные недоступны" action={<button className="button" type="button" onClick={() => void reload()}>Попробовать снова</button>}>
          {error}
        </EmptyState>
      </main>
    );
  }

  switch (route.name) {
    case 'home': return <HomePage />;
    case 'station': return <StationPage stationId={route.stationId} />;
    case 'place': return <PlacePage placeId={route.placeId} />;
    case 'new-place': return <PlaceFormPage initialStationId={route.stationId} />;
    case 'edit-place': return <PlaceFormPage placeId={route.placeId} />;
    case 'settings': return <SettingsPage />;
    default:
      return (
        <main className="page">
          <EmptyState title="Страница не найдена" action={<a className="button" href="#/">На главную</a>}>
            Проверьте адрес или вернитесь к списку станций.
          </EmptyState>
        </main>
      );
  }
}

export default function App() {
  const [updateWorker, setUpdateWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    registerServiceWorker(setUpdateWorker);
  }, []);

  return (
    <AppProvider>
      <div className="app-shell">
        <AppContent />
      </div>
      {updateWorker ? (
        <div className="toast" role="status">
          <span>Доступна новая версия</span>
          <button className="button" type="button" onClick={() => activateServiceWorker(updateWorker)}>Обновить</button>
        </div>
      ) : null}
    </AppProvider>
  );
}
